import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import * as _ from 'lodash';
import moment from 'moment';
import { Observable, of } from 'rxjs';
import { mapTo } from 'rxjs/operators';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';
import { ConfirmationTimeRecordingModel } from '../../models/complex/confirmation-time-recording.model';
import {
    TIMERECORDING_TYPE_OF_WORK_CODES,
    TimeRecordingEntryModel,
} from '../../models/complex/time-recording-entry.model';
import {
    isTimeRecordingGeneralEntryModel,
    TimeRecordingGeneralEntryModel,
} from '../../models/complex/time-recording-general-entry.model';
import {
    isTimeRecordingLimitedEntryModel,
    TimeRecordingLimitedEntryModel,
} from '../../models/complex/time-recording-limited-entry.model';
import { NotificationIdentifierModel } from '../../models/low-level/notification-identifier.model';
import { ConfigService } from '../config/config.service';
import { TimesheetAndExpensesService } from '../timesheet/timesheet-and-expenses.service';

@Injectable({
    providedIn: 'root',
})
export class TimeRecordingService {
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private timesheetAndExpensesService: TimesheetAndExpensesService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private readonly configService: ConfigService,
    ) {}

    public insertEntry(entries: TimeRecordingEntryModel[], newEntry: TimeRecordingEntryModel): void {
        const isGapAllowedBetweenEntries = this.configService.getAllowNotificationPause();
        // Special case for standby travel. Where user can add additional travel Entry.
        // the additional travel entry is added in the 2nd position, after the first travel entry by default.
        if (newEntry instanceof TimeRecordingLimitedEntryModel && newEntry.isTravelToEntry()) {
            entries.splice(1, 0, newEntry);
            return;
        }
        // Case #9: new entry starts at the same time as TRBK
        // Case #10: new entry starts at the same time as an existing entry
        if (this.hasInsertedEntryAtSameTimeAsAnotherEntry(entries, newEntry)) {
            return;
        }

        entries = this.insertAndSort(entries, newEntry);

        // Case #4: new entry fits completely into a non-TRAV entry

        for (const currentEntry of entries) {
            if (
                !currentEntry.isTravelToEntry() &&
                newEntry !== currentEntry &&
                currentEntry.getTypeOfWorkCode() !== TIMERECORDING_TYPE_OF_WORK_CODES.TRBK &&
                newEntry.startDateTime.isAfter(currentEntry.startDateTime, 'minute') &&
                newEntry.endDateTime.isSameOrBefore(currentEntry.endDateTime, 'minute')
            ) {
                // Remember the old end time.
                const endDateTimeBeforeChange = currentEntry.endDateTime.clone();

                // Add another entry which starts at the end of the new entry and has the same end time as the initial entry before the split.
                const additionalEntry = (<TimeRecordingGeneralEntryModel>currentEntry).clone();
                additionalEntry.setStartDateTime(newEntry.endDateTime);
                additionalEntry.setEndDateTime(endDateTimeBeforeChange);

                // Adopt end of the entry which is affected by the new entries start time.
                currentEntry.setEndDateTime(newEntry.startDateTime);

                // Only insert the additionalEntry to the entries Array if it has a duration - empty entries will not be inserted
                const duration = additionalEntry.endDateTime.diff(additionalEntry.startDateTime);
                if (duration > 0) {
                    entries = this.insertAndSort(entries, additionalEntry);
                }

                return;
            }
        }

        // Case #2: New entry fits completely into TRAV (TRTO)
        // Case #3: new entry spans from TRTO into another entry
        // Case #5: new entry spans over multiple entries
        // Case #6: new entry spans into TRBK
        let difference = 0;
        let subsequentEntries: TimeRecordingEntryModel[] = [];
        for (const currentEntry of entries) {
            if (
                newEntry !== currentEntry &&
                newEntry.startDateTime.isSameOrAfter(currentEntry.startDateTime, 'minute') &&
                newEntry.startDateTime.isSameOrBefore(currentEntry.endDateTime, 'minute')
            ) {
                subsequentEntries = this.getSubsequentEntries(entries, newEntry);

                const subsequentEntry = this.getSubsequentEntry(entries, newEntry);

                // when you add a phone fix entry, there is no subsequent entry
                if (subsequentEntry) {
                    difference = newEntry.endDateTime.diff(subsequentEntry.startDateTime);

                    const precedingEntry = this.getPrecedingEntry(entries, newEntry);
                    if (precedingEntry) {
                        precedingEntry.setEndDateTime(newEntry.startDateTime);
                    }
                    break;
                }
            }
        }
        if (subsequentEntries.length > 0) {
            this.updateSubsequentEntries(subsequentEntries, newEntry, isGapAllowedBetweenEntries, difference);
        }
    }

    private insertAndSort(
        entries: TimeRecordingEntryModel[],
        newEntry: TimeRecordingEntryModel,
    ): TimeRecordingEntryModel[] {
        let travelToEntry: TimeRecordingEntryModel | undefined;
        const travelToEntries = this.getTravelToEntries(entries);
        const enableStandbyTravel = this.configService.getEnableStandbyTravel();

        if (enableStandbyTravel) {
            entries.splice(0, travelToEntries.length);
        } else {
            travelToEntry = entries[0];
            if (travelToEntry && travelToEntry.getTypeOfWorkCode() === TIMERECORDING_TYPE_OF_WORK_CODES.TRTO) {
                entries.shift();
            }
        }

        const entryTRBK = entries[entries.length - 1];
        if (entryTRBK && entryTRBK.getTypeOfWorkCode() === TIMERECORDING_TYPE_OF_WORK_CODES.TRBK) {
            entries.pop();
        }

        entries.push(newEntry);
        entries.sort((timeRecEntryA: TimeRecordingEntryModel, timeRecEntryB: TimeRecordingEntryModel) => {
            if (timeRecEntryA.startDateTime.isBefore(timeRecEntryB.startDateTime)) {
                return -1;
            }
            if (timeRecEntryA.startDateTime.isAfter(timeRecEntryB.startDateTime)) {
                return 1;
            }
            if (timeRecEntryA.endDateTime.isBefore(timeRecEntryB.endDateTime)) {
                return -1;
            }
            if (timeRecEntryA.endDateTime.isAfter(timeRecEntryB.endDateTime)) {
                return 1;
            }
            return 0;
        });

        if (enableStandbyTravel) {
            entries.unshift(...travelToEntries);
        } else if (travelToEntry && travelToEntry.getTypeOfWorkCode() === TIMERECORDING_TYPE_OF_WORK_CODES.TRTO) {
            entries.unshift(travelToEntry);
        }
        if (entryTRBK && entryTRBK.getTypeOfWorkCode() === TIMERECORDING_TYPE_OF_WORK_CODES.TRBK) {
            entries.push(entryTRBK);
        }

        return entries;
    }

    private updateSubsequentEntries(
        entries: TimeRecordingEntryModel[],
        newEntry: TimeRecordingEntryModel,
        isGapAllowedBetweenEntries: boolean,
        difference: number,
    ): void {
        let previousEntry: TimeRecordingEntryModel;
        entries.map((currentEntry: TimeRecordingEntryModel) => {
            if (
                !isGapAllowedBetweenEntries ||
                (isGapAllowedBetweenEntries &&
                    (currentEntry.startDateTime.isBefore(newEntry.endDateTime) ||
                        (previousEntry && currentEntry.startDateTime.isBefore(previousEntry?.endDateTime))))
            ) {
                const newStartDateTime = currentEntry.startDateTime.clone().add(difference);
                currentEntry.startDateTime = newStartDateTime;
                currentEntry.updateEndDateTime();
            }
            previousEntry = currentEntry;
        });
    }

    private hasInsertedEntryAtSameTimeAsAnotherEntry(
        entries: TimeRecordingEntryModel[],
        newEntry: TimeRecordingEntryModel,
    ): boolean {
        const isGapAllowedBetweenEntries = this.configService.getAllowNotificationPause();
        let entryTRBK: TimeRecordingEntryModel | undefined;
        let subsequentEntries: TimeRecordingEntryModel[] = [];

        for (const currentEntry of entries) {
            if (newEntry.startDateTime.isSame(currentEntry.startDateTime, 'minute')) {
                subsequentEntries = this.getSubsequentEntries(entries, currentEntry);
                const subsequentEntry = this.getSubsequentEntry(entries, currentEntry);
                // don't forget to add also the currentEntry to the subsequentEntries,
                // as this entry is not detected by the method getSubsequentEntries
                // add the currentEntry before the subsequent entries
                // only add the currentEntry if the currentEntry is not TRBK or TRTO

                const typeOfWorkCode = currentEntry.getTypeOfWorkCode();
                if (typeOfWorkCode !== TIMERECORDING_TYPE_OF_WORK_CODES.TRBK && !currentEntry.isTravelToEntry()) {
                    subsequentEntries.unshift(currentEntry);
                }
                if (subsequentEntry && subsequentEntry.isTravelToEntry()) {
                    subsequentEntries.shift();
                }

                entryTRBK =
                    currentEntry.getTypeOfWorkCode() === TIMERECORDING_TYPE_OF_WORK_CODES.TRBK
                        ? currentEntry
                        : undefined;
                break;
            }
        }

        // if there are subsequent entries then adopt the times and durations of them
        if (subsequentEntries.length > 0) {
            // if the duration of the new entry is 0, we don't need to apply anything to subsequent entries
            const duration = newEntry.endDateTime.diff(newEntry.startDateTime);
            if (duration > 0) {
                const difference = newEntry.endDateTime.diff(newEntry.startDateTime);
                this.updateSubsequentEntries(subsequentEntries, newEntry, isGapAllowedBetweenEntries, difference);
            }
        }

        // in case we add a new entry at the same time as TRBK, there is no subsequent entry, but we still want to add the entry and
        // we want to update the TRBK entry and move it by the duration of the new entry.
        if (entryTRBK) {
            // if the duration of the new entry is 0, we dont need to apply anything to subsequent entries
            const duration = newEntry.endDateTime.diff(newEntry.startDateTime);
            if (duration > 0) {
                const newStartDateTime = entryTRBK.startDateTime.clone().add(duration);
                entryTRBK.startDateTime = newStartDateTime;
                entryTRBK.updateEndDateTime();
            }
            entries = this.insertAndSort(entries, newEntry);
            return true;
        }

        return false;
    }

    public deleteEntry(timeRecordingModel: ConfirmationTimeRecordingModel, index: number): void {
        const precedingEntry = this.getPrecedingEntry(
            timeRecordingModel.timeRecordingEntries.value,
            timeRecordingModel.timeRecordingEntries.value[index],
        );

        // remove the entry first to not have it in the loop of belows code
        timeRecordingModel.removeTimeRecordingEntry(index);

        if (precedingEntry) {
            // iterate through all subsequent entries and change the startTime of those including their activities
            // but pass in the preceding entry of the deleted one to take that as reference for the startDateTime calculation
            this.changeStartTimeForSubsequentEntries(timeRecordingModel.timeRecordingEntries.value, precedingEntry);
        }
    }

    /**
     * If the startTime of an entry got changed, we adopt the duration of the same row to try to get a consistent state again.
     * This only works until we reached a duration of 0 for this entry. From this point on there is no automatic manipulation
     * done anymore. The user needs to correct those heavily wrong entries first, to enable the automatic support again.
     * Furthermore the end time and duration of the preceding value is updated to get a consistent state again.
     *
     * @param notificationIdentifier
     * @param entries
     * @param changedTimeRecEntry
     * @param isRemoteWork
     */
    public changeDurationForSingleEntry(
        notificationIdentifier: NotificationIdentifierModel,
        entries: TimeRecordingEntryModel[],
        changedTimeRecEntry: TimeRecordingEntryModel,
        isRemoteWork = false,
    ): void {
        const isGapAllowedBetweenEntries = this.configService.getAllowNotificationPause();
        const typeOfWorkCode = changedTimeRecEntry.getTypeOfWorkCode();
        let subsequentEntry: TimeRecordingEntryModel | undefined = this.getSubsequentEntry(
            entries,
            changedTimeRecEntry,
        );
        let precedingEntry: TimeRecordingEntryModel | undefined = this.getPrecedingEntry(entries, changedTimeRecEntry);

        // The endDateTime of the changedEntry needs to be adapted only if the start of the changedTimeRecEntry
        // is after the end of the preceding entry. Otherwise, the end should not be updated.
        // If the entry is a phoneFix, then update the endDateTime every time the startDateTime is updated
        // A TRTO entry will not have a preceding entry
        if (
            (typeof precedingEntry === 'undefined' && changedTimeRecEntry.isTravelToEntry()) ||
            (typeof precedingEntry !== 'undefined' &&
                changedTimeRecEntry.startDateTime.isSameOrAfter(precedingEntry.endDateTime)) ||
            (this.clientManager.isNgmP58() &&
                changedTimeRecEntry instanceof TimeRecordingGeneralEntryModel &&
                (isRemoteWork || changedTimeRecEntry.isPhoneFix()))
        ) {
            changedTimeRecEntry.updateEndDateTime();
        }
        if (changedTimeRecEntry.endTimeModel.value.isValid() && isTimeRecordingGeneralEntryModel(changedTimeRecEntry)) {
            changedTimeRecEntry.setIsDurationUnchanged(false);
        }

        if (typeOfWorkCode === TIMERECORDING_TYPE_OF_WORK_CODES.TRBK) {
            // If TRBK startdatetime is changed such that it is before the TRTO startdatetime then subsequentEntry becomes TRTO and preceding entry is undefined
            // and duration of actual preceding(OSR) entry will not get adjusted correctly.
            // To fix this scenario we have to force subsequentEntry to be null and find out preceding entry of TRBK irrespective of entries start/end datetime
            subsequentEntry = undefined;
            precedingEntry = this.getPrecedingEntryForTRBK(entries, changedTimeRecEntry);
            if (
                precedingEntry &&
                (!isGapAllowedBetweenEntries ||
                    (isGapAllowedBetweenEntries &&
                        precedingEntry.endDateTime.isAfter(changedTimeRecEntry.startDateTime)))
            ) {
                precedingEntry.setEndDateTime(changedTimeRecEntry.startDateTime);
            }
            changedTimeRecEntry.updateEndDateTime();
        }

        // being on the last TRAV (travel back) row will have no subsequent entry, but that's ok, we dont need to adapt the duration then
        if (subsequentEntry) {
            // if the currents entry startTime is after the next entries startTime, we do not adapt the duration because
            // this situation is invalid. The user needs to correct it before we offer more automatism again.
            // We need to update the startDateTime of the subsequentEntry with the endDateTime of the changedTimeRecEntry,
            // otherwise we will not able to calculate the duration correctly of the changedTimeRecEntry
            if (
                (isGapAllowedBetweenEntries &&
                    subsequentEntry.startDateTime.isBefore(changedTimeRecEntry.endDateTime)) ||
                (!isGapAllowedBetweenEntries && !subsequentEntry.startDateTime.isSame(changedTimeRecEntry.endDateTime))
            ) {
                subsequentEntry.startDateTime = changedTimeRecEntry.endDateTime;
                // If there are more than 1 subsequent entries, then start time for all of them has to be adapted
                if (typeOfWorkCode !== TIMERECORDING_TYPE_OF_WORK_CODES.TRBK) {
                    this.changeStartTimeForSubsequentEntries(entries, subsequentEntry);
                }
            }
            if (changedTimeRecEntry.startDateTime.isSameOrBefore(subsequentEntry.startDateTime)) {
                changedTimeRecEntry.updateEndDateTime();
            }
        }

        if (precedingEntry) {
            // Imagine following scenario:
            // TRTO 09:00 - 10:20
            // OSR  10:20 - 11:20
            // TRBK 11:20 - 11:20
            // If the start time of TRBK (changedTimeRecEntry) is changed to 10:20, the preceding entry of it is OSR.
            // The end time is set correctly then and we have following scenario:
            // TRTO 09:00 - 10:20
            // OSR  10:20 - 10:20
            // TRBK 10:20 - 11:20
            // Now TRBK is changed again to 09:20. This means that the preceding entry is now TRTO. We dont want to change
            // the end time of TRTO that's why we need to check if the preceding entry has a subsequent entry. Counting
            // from 09:00 the subsequent entry is the OSR entry, because the getSubsequentEntry() starts searching from
            // index = 0 of the entries array. The array has the order TRTO, OSR, TRBK and the first entry that fits the
            // condition (startDateTime.isAfter(TRTO)) is the OSR entry.
            // That means that between TRTO and the changed entry is another entry, so we dont want to update TRTOs
            // end time in that case. We only do that if we are working on two entries right next to each other.
            const subsequentEntryForPrecedingEntry = this.getSubsequentEntry(entries, precedingEntry);
            if (subsequentEntryForPrecedingEntry === changedTimeRecEntry) {
                // if the changedEntry start DateTime is before the precedingEntry
                // then we should set on the endDatetime of the preceding entry the startDateTime of the preceding Entry
                // Otherwise we need to set the endDateTime of the precedingEntry to the startDateTime of the changedTimeRecEntry
                if (changedTimeRecEntry.startDateTime.isBefore(precedingEntry.startDateTime)) {
                    precedingEntry.startDateTime = changedTimeRecEntry.startDateTime;
                    precedingEntry.setEndDateTime(changedTimeRecEntry.startDateTime);
                } else if (
                    !isGapAllowedBetweenEntries ||
                    changedTimeRecEntry.startDateTime.isSame(precedingEntry.startDateTime) ||
                    changedTimeRecEntry.startDateTime.isBefore(precedingEntry.endDateTime)
                ) {
                    precedingEntry.setEndDateTime(changedTimeRecEntry.startDateTime);
                }
            }
        }
        this.sortTimeRecordingEntries(entries);
    }

    // P58: If it is the first time recording of the day, and the start time of first entry is before current SOW,
    // and after the previous EOW, then SOW should automatically be adjusted to the start time of first entry.
    public updateTimesheetStartOfWork(
        notificationIdentifier: NotificationIdentifierModel,
        entries: TimeRecordingEntryModel[],
        changedTimeRecEntry: TimeRecordingEntryModel,
    ): Observable<void> {
        this.sortTimeRecordingEntries(entries);
        const firstEntry = entries[0];
        if (changedTimeRecEntry === firstEntry) {
            const startOfWork = this.timesheetAndExpensesService.getStartOfWork();
            const currentTimesheet = this.timesheetAndExpensesService.getCurrentTimesheet();
            const submittedTimesheet = this.timesheetAndExpensesService.getSubmittedTimesheet();
            const endOfWork = submittedTimesheet.getEndOfWork();

            // Start-of-Works needs to be set to the first entry start time if there is either no current timesheet notification
            // or if there is just a single one.(ignoring the timesheet-notification created for additional expenses(qmNum='', maNum=''))
            // A single one can be there if the confirmation has been saved already.
            // So we need to check if there are no other confirmed notifications. SHCNGMP58-537
            let noneOrSameNotification = currentTimesheet.notifications.length === 0;
            if (currentTimesheet.notifications.length) {
                const otherNotification = currentTimesheet.notifications.find(
                    (notification) =>
                        notification.notificationId.qmNum &&
                        notification.isConfirmationSubmitted &&
                        !notification.notificationId.equalsTo(notificationIdentifier),
                );
                if (!otherNotification) {
                    noneOrSameNotification = true;
                }
            }
            if (
                noneOrSameNotification &&
                firstEntry.startDateTime.isBefore(startOfWork) &&
                (!endOfWork.isValid() || firstEntry.startDateTime.isSameOrAfter(endOfWork))
            ) {
                this.log.debug('SOW changed due to a change to the first entry of the day in time recording.');
                return this.timesheetAndExpensesService
                    .saveStartOfWork(firstEntry.startDateTime.clone())
                    .pipe(mapTo(undefined));
            }
        }
        return of(undefined);
    }

    /**
     * If the duration of an entry got changed, we move the startTime of all subsequent entries by the same amount.
     * This method also takes care for processing all activities of an  subsequent entry. It is not changing the startTimes
     * of the activities of the changedTimeRecEntry. If the parameter changedActivityEntry is non-empty, additional code
     * is executed that takes care for updating the duration of the parent entry too. Furthermore it means that the duration
     * of the last activity has been changed. This results in updating the subsequent entry of the parent entry of that
     * activity, which is changedTimeRecEntry itself.
     *
     * @param {TimeRecordingEntryModel[]} entries
     * @param {TimeRecordingEntryModel} changedTimeRecEntry
     */
    public changeStartTimeForSubsequentEntries(
        entries: TimeRecordingEntryModel[],
        changedTimeRecEntry: TimeRecordingEntryModel,
    ): void {
        const isGapAllowedBetweenEntries = this.configService.getAllowNotificationPause();
        const typeOfWorkCode = changedTimeRecEntry.getTypeOfWorkCode();
        // The duration of the changed entry is changed within the duration stepper component and the model itself.
        // The end time of that specific entry would never get updated if we dont do it manually as startDateTime is
        // never set via the setter function which has the logic to update endDateTime.
        changedTimeRecEntry.updateEndDateTime();

        // This is ugly but necessary. We need to store if the duration value has been changed in order to set its
        // value to null. If we dont do that it will be calculated as 0 and will be valid in the form .
        // But TRBK must be changed by a user manually.
        if (changedTimeRecEntry.endTimeModel.value.isValid()) {
            if (
                isTimeRecordingLimitedEntryModel(changedTimeRecEntry) &&
                typeOfWorkCode === TIMERECORDING_TYPE_OF_WORK_CODES.TRBK
            ) {
                changedTimeRecEntry.setIsDurationUnchanged(false);
            } else if (isTimeRecordingGeneralEntryModel(changedTimeRecEntry)) {
                changedTimeRecEntry.setIsDurationUnchanged(false);
            }
        }

        const subsequentEntriesArray = this.getSubsequentEntries(entries, changedTimeRecEntry);

        // being on the last TRAV (travel back) row will have no subsequent entry, but that's ok, we dont need to adapt the duration then
        if (subsequentEntriesArray.length > 0) {
            let lastEntry = changedTimeRecEntry;
            subsequentEntriesArray.forEach((subsequentEntry: TimeRecordingEntryModel) => {
                // calculate the new startTime of the next entry by adding the duration to the startTime of the lastly looped entry
                const nextStartTime = lastEntry.endDateTime.clone();

                if (
                    !isGapAllowedBetweenEntries ||
                    (isGapAllowedBetweenEntries && subsequentEntry.startDateTime.isBefore(nextStartTime))
                ) {
                    // We do not replace the reference, we rather change in-place.
                    subsequentEntry.startDateTime = nextStartTime;
                    subsequentEntry.updateEndDateTime();
                }

                // in order to have a reference to the preceding entry in the next loop, we store that now
                lastEntry = subsequentEntry;
            });
        }
        this.sortTimeRecordingEntries(entries);
    }

    public setBillableCodeForAllEntries(entries: TimeRecordingEntryModel[], selectedBillablePresetCode: string): void {
        entries.forEach((currentEntry: TimeRecordingEntryModel) => {
            if (
                currentEntry instanceof TimeRecordingGeneralEntryModel ||
                currentEntry instanceof TimeRecordingLimitedEntryModel
            ) {
                currentEntry.billableModel.selectByValue(selectedBillablePresetCode);
            }
        });
    }

    public getPrecedingEntry(
        entries: TimeRecordingEntryModel[],
        referenceEntry: TimeRecordingEntryModel,
    ): undefined | TimeRecordingEntryModel {
        const entryIndex = entries.findIndex((currentEntry) => currentEntry === referenceEntry);
        let preceedingEntry = entryIndex - 1 < 0 ? undefined : entries[entryIndex - 1];
        const enableStandbyTravel = this.configService.getEnableStandbyTravel();

        // In case we have an invalid timeRecording we could have the situation that TRBK is before TRTO. In this case TRBK
        // is returned instead of TRTO and the automatic adaption of duration doesnt work anymore.
        // So we make sure that TRTO is returned if we have just a single OSR entry and this one is the reference entry.
        if (
            referenceEntry.getTypeOfWorkCode() === TIMERECORDING_TYPE_OF_WORK_CODES.OSR &&
            this.countEntriesByType(entries, TIMERECORDING_TYPE_OF_WORK_CODES.OSR) === 1
        ) {
            const maxTravelEntriesForKorea = 2;
            const precedingEntries = enableStandbyTravel
                ? this.getTravelToEntries(entries)
                : this.getEntriesByType(TIMERECORDING_TYPE_OF_WORK_CODES.TRTO, entries);
            if (
                precedingEntries.length === 1 ||
                (enableStandbyTravel && precedingEntries.length <= maxTravelEntriesForKorea)
            ) {
                preceedingEntry = precedingEntries.pop();
            }
        }

        return preceedingEntry;
    }

    /**
     * Returns the last OSR entry based only on index and irrespective of start/end datetime
     * This method is required to find out the preceding entry for TRBK for scenarios when start datetimes are not in a consistent state
     *
     * @param {TimeRecordingEntryModel[]} entries
     * @param {TimeRecordingEntryModel} referenceEntry
     * @returns {TimeRecordingEntryModel}
     */
    public getPrecedingEntryForTRBK(
        entries: TimeRecordingEntryModel[],
        referenceEntry: TimeRecordingEntryModel,
    ): TimeRecordingEntryModel | undefined {
        return entries.findLast((currentEntry: TimeRecordingEntryModel) => {
            return currentEntry !== referenceEntry;
        });
    }

    public getSubsequentEntry(
        entries: TimeRecordingEntryModel[],
        referenceEntry: TimeRecordingEntryModel,
    ): TimeRecordingEntryModel | undefined {
        if (entries.length > 1) {
            const entryIndex = entries.findIndex((currentEntry) => currentEntry === referenceEntry);
            return entryIndex + 1 >= entries.length ? undefined : entries[entryIndex + 1];
        }
        return undefined;
    }

    /**
     * Returns all subsequent time recording entries based on the contextualIndex. All entries
     * with a higher id will be returned.
     * Relies on a sorted list of entries.
     *
     * @param entries
     * @param referenceEntry
     */
    public getSubsequentEntries(
        entries: TimeRecordingEntryModel[],
        referenceEntry: TimeRecordingEntryModel,
    ): TimeRecordingEntryModel[] {
        const entryIndex = entries.findIndex((currentEntry) => currentEntry === referenceEntry);
        // get all entries after the index of the referenceEntry
        return entries.slice(entryIndex + 1);
    }

    /**
     * Returns the time recording entry with the lowest id, which means that its the first entry of the collection.
     *
     * @param entries
     */
    public getFirstTimeRecordingEntry(entries: TimeRecordingEntryModel[]): TimeRecordingEntryModel | undefined {
        return _.minBy(entries, (currentTimeRecEntry: TimeRecordingEntryModel) => {
            return currentTimeRecEntry.startDateTime.valueOf();
        });
    }

    /**
     * Returns the time recording entry with the highest id, which means that its the last entry of the collection.
     *
     * @param entries
     */
    public getLastTimeRecordingEntry(entries: TimeRecordingEntryModel[]): TimeRecordingEntryModel | undefined {
        return entries[entries.length - 1];
    }

    /**
     * Mutating, in-place sort. It changes the order of the entries within the passed reference. It must not set an new reference.
     * It sorts the entries by the property id, which is a incrementing number with the possibility of gaps.
     *
     * @param {TimeRecordingEntryModel[]} entries
     */
    public sortTimeRecordingEntries(entries: TimeRecordingEntryModel[]): void {
        let travelToEntry: TimeRecordingEntryModel | undefined;
        const travelToEntries = this.getTravelToEntries(entries);
        const enableStandbyTravel = this.configService.getEnableStandbyTravel();

        if (enableStandbyTravel) {
            entries.splice(0, travelToEntries.length);
        } else {
            travelToEntry = entries[0];
            if (travelToEntry && travelToEntry.getTypeOfWorkCode() === TIMERECORDING_TYPE_OF_WORK_CODES.TRTO) {
                entries.shift();
            }
        }
        const trbk = entries[entries.length - 1];
        if (trbk && trbk.getTypeOfWorkCode() === TIMERECORDING_TYPE_OF_WORK_CODES.TRBK) {
            entries.pop();
        }
        entries.sort((timeRecEntryA: TimeRecordingEntryModel, timeRecEntryB: TimeRecordingEntryModel) => {
            if (timeRecEntryA.startDateTime.isBefore(timeRecEntryB.startDateTime)) {
                return -1;
            }
            if (timeRecEntryA.startDateTime.isAfter(timeRecEntryB.startDateTime)) {
                return 1;
            }
            if (timeRecEntryA.endDateTime.isBefore(timeRecEntryB.endDateTime)) {
                return -1;
            }
            if (timeRecEntryA.endDateTime.isAfter(timeRecEntryB.endDateTime)) {
                return 1;
            }
            return 0;
        });

        if (enableStandbyTravel) {
            entries.unshift(...travelToEntries);
        } else if (travelToEntry && travelToEntry.getTypeOfWorkCode() === TIMERECORDING_TYPE_OF_WORK_CODES.TRTO) {
            entries.unshift(travelToEntry);
        }
        if (trbk && trbk.getTypeOfWorkCode() === TIMERECORDING_TYPE_OF_WORK_CODES.TRBK) {
            entries.push(trbk);
        }
    }

    public countEntriesByType(
        entries: TimeRecordingEntryModel[],
        typeOfWork: TIMERECORDING_TYPE_OF_WORK_CODES,
    ): number {
        const countResult = _.countBy(entries, (entry: TimeRecordingEntryModel) => {
            return entry.getTypeOfWorkCode();
        });

        return countResult[typeOfWork] ? countResult[typeOfWork] : 0;
    }

    public getEntriesByType(
        typeCode: TIMERECORDING_TYPE_OF_WORK_CODES,
        entries: TimeRecordingEntryModel[],
    ): TimeRecordingEntryModel[] {
        return entries.filter((entry) => entry.getTypeOfWorkCode() === typeCode);
    }

    /**
     * Filters the time recording entries for list of work codes
     *
     * @param {TIMERECORDING_TYPE_OF_WORK_CODES[]} typeCodes
     * @param {TimeRecordingEntryModel[]} entries
     * @returns {TimeRecordingEntryModel[]}
     */
    public getEntriesByMultipleTypes(
        typeCodes: TIMERECORDING_TYPE_OF_WORK_CODES[],
        entries: TimeRecordingEntryModel[],
    ): TimeRecordingEntryModel[] {
        return entries.filter((entry) => {
            const typeOfWorkCode = entry.getTypeOfWorkCode();
            if (!typeOfWorkCode) {
                return false;
            }
            return typeCodes.indexOf(typeOfWorkCode) !== -1;
        });
    }

    /**
     * Filters all the OSR time recording entries, works for both P58 (workcode OSR) and NGMAA (all workcodes)
     *
     * @param {TimeRecordingEntryModel[]} entries
     * @returns {TimeRecordingEntryModel[]}
     */
    public getOSREntries(entries: TimeRecordingEntryModel[]): TimeRecordingGeneralEntryModel[] {
        return <TimeRecordingGeneralEntryModel[]>(
            this.getEntriesByMultipleTypes(
                [
                    TIMERECORDING_TYPE_OF_WORK_CODES.APPLICATION,
                    TIMERECORDING_TYPE_OF_WORK_CODES.APPLICATION_BILLABLE,
                    TIMERECORDING_TYPE_OF_WORK_CODES.OSR,
                    TIMERECORDING_TYPE_OF_WORK_CODES.OSR_BILLABLE,
                    TIMERECORDING_TYPE_OF_WORK_CODES.APPLICATION_ECBD,
                    TIMERECORDING_TYPE_OF_WORK_CODES.ECBD,
                    TIMERECORDING_TYPE_OF_WORK_CODES.SALES_SUPPORT,
                ],
                entries,
            )
        );
    }

    public getTravelToEntries(entries: TimeRecordingEntryModel[]): TimeRecordingLimitedEntryModel[] {
        return <TimeRecordingLimitedEntryModel[]>(
            this.getEntriesByMultipleTypes(
                [TIMERECORDING_TYPE_OF_WORK_CODES.TRTO, TIMERECORDING_TYPE_OF_WORK_CODES.TRAVEL_TO_STANDBY],
                entries,
            )
        );
    }

    public getNonTravEntries(entries: TimeRecordingEntryModel[]): TimeRecordingGeneralEntryModel[] {
        return <TimeRecordingGeneralEntryModel[]>(
            this.getEntriesByMultipleTypes(
                [
                    TIMERECORDING_TYPE_OF_WORK_CODES.APPLICATION,
                    TIMERECORDING_TYPE_OF_WORK_CODES.APPLICATION_BILLABLE,
                    TIMERECORDING_TYPE_OF_WORK_CODES.OSR,
                    TIMERECORDING_TYPE_OF_WORK_CODES.OSR_BILLABLE,
                    TIMERECORDING_TYPE_OF_WORK_CODES.APPLICATION_ECBD,
                    TIMERECORDING_TYPE_OF_WORK_CODES.ECBD,
                    TIMERECORDING_TYPE_OF_WORK_CODES.BREAK,
                    TIMERECORDING_TYPE_OF_WORK_CODES.WAIT,
                    TIMERECORDING_TYPE_OF_WORK_CODES.PHONE_FIX,
                    TIMERECORDING_TYPE_OF_WORK_CODES.PHONE_FIX_EXPENSES,
                    TIMERECORDING_TYPE_OF_WORK_CODES.SALES_SUPPORT,
                ],
                entries,
            )
        );
    }

    public getMaximumValueForTimeOfRecovery(
        entries: TimeRecordingEntryModel[],
        isNotificationRemoteWork = false,
    ): moment.Moment {
        let maxValue = moment();
        if (isNotificationRemoteWork) {
            // If i t is Remote work, then get the end time of last entry
            const lastEntry = entries[entries.length - 1];
            if (lastEntry instanceof TimeRecordingGeneralEntryModel) {
                maxValue = lastEntry.endDateTime.clone();
            }
        } else if (entries.length > 1) {
            // NGMP58 non Phone-Fix case, // other countries all cases
            const osrEntries = this.getOSREntries(entries);
            if (osrEntries.length === 0) {
                // In case of no OSR entry, the start date time of Travel Back entry is the maxValue
                // as the start date time of TRBK is also the end date time of Phone-Fix/Wait/Break
                const travelBack = this.getEntriesByType(TIMERECORDING_TYPE_OF_WORK_CODES.TRBK, entries);
                maxValue = travelBack[0].startDateTime.clone();
            } else if (osrEntries.length === 1) {
                // In case of only one OSR entry, the end date time of OSR entry is the maxValue
                maxValue = osrEntries[0].endDateTime.clone();
            } else {
                // In case of multiple OSR entries, the end date time of last entry is the maxValue
                osrEntries.sort((entry1, entry2) => (entry1.startDateTime.isBefore(entry2.startDateTime) ? -1 : 1));
                maxValue = osrEntries[osrEntries.length - 1].endDateTime.clone();
            }
        } else if (entries[0] instanceof TimeRecordingGeneralEntryModel && entries[0].isPhoneFix()) {
            // only NGMP58 Phone-Fix case
            maxValue = entries[0].endDateTime.clone();
        } else {
            this.log.error(`Invalid Time Recording entries present in confirmation`);
            throw new Error(
                `TimeRecordingService: getMaximumValueForTimeOfRecovery: Invalid Time Recording entries present in confirmation`,
            );
        }
        return maxValue;
    }
}
